




import * as d3 from "d3";
import crossfilter from "crossfilter2";
import { Component, Emit, Prop, Vue, Watch } from "vue-property-decorator";

import { Match, Record } from "@/types";

@Component({})
export default class TimelineData extends Vue {
  @Prop()
  private records!: Record[];

  @Prop()
  private range!: [Date, Date];

  @Emit("select")
  select(value: Match) {}

  height = 600;

  padding = { top: 30, bot: 20, left: 90, right: 15 };

  PADDING_TOP_GRID = 5;

  mounted() {
    this.$nextTick(this.draw);
  }

  get width() {
    return this.$el.clientWidth;
  }

  get filtered() {
    return crossfilter(this.records)
      .dimension(record => record.date)
      .filter(this.range)
      .top(Infinity);
  }

  get xScale() {
    return d3
      .scaleLinear()
      .domain(this.range)
      .range([this.padding.left, this.width - this.padding.right - 10]);
  }

  get yScale() {
    const domain = crossfilter(this.filtered)
      .dimension(record => record.name)
      .group()
      .top(Infinity);

    return (
      this.filtered &&
      d3
        .scaleBand()
        .range([this.padding.top, this.padding.top + 30 * domain.length])
        .domain(domain.map(e => e.key as string))
        .paddingOuter(0.5)
    );
  }

  @Watch("records")
  @Watch("range")
  @Watch("height")
  @Watch("padding", { deep: true })
  draw() {
    if (this.filtered && this.filtered.length === 0) {
      d3.select(this.$el as Element).html(
        `<div class="my-4 text-center">There are no data to be shown in the selected period.</div>`
      );
    }

    if (
      !this.records ||
      !this.range ||
      !this.height ||
      !this.padding ||
      !this.filtered ||
      this.filtered.length === 0 ||
      !this.xScale ||
      !this.yScale
    ) {
      return;
    }

    const canvas = d3
      .select(this.$el as Element)
      .html("")
      .append("svg")
      .attr("width", this.width)
      .attr("height", this.height)
      .style("overflow", "visible");

    canvas
      .append("g")
      .attr("class", "x--axis")
      .attr("transform", "translate(0," + this.padding.top + ")")
      .call(
        d3
          .axisTop(this.xScale)
          .ticks(5)
          .tickFormat(d3.timeFormat("%Y %b %d") as any)
      );

    this.padding.left = Math.max(
      ...canvas
        .append("g")
        .attr("class", "grid y--axis")
        .attr(
          "transform",
          `translate(${this.padding.left}, ${this.PADDING_TOP_GRID})`
        )
        .call(
          d3
            .axisLeft(this.yScale)
            .tickSize(-this.width + this.padding.left + this.padding.right)
        )
        .selectAll("text")
        .attr("transform", `translate(-5, -${this.yScale.bandwidth() / 2})`)
        .nodes()
        .map(el => (el as SVGTextElement).getBBox().width + 9)
    );

    canvas
      .selectAll(".data")
      .data(
        crossfilter(this.filtered)
          .dimension((d: any) => d.name)
          .top(Infinity)
      )
      .enter()
      .append("circle")
      .attr("class", "data--change")
      .attr("style", "cursor: pointer")
      .attr("cx", d => this.xScale && this.xScale(d.date))
      .attr(
        "cy",
        d => ((this.yScale && this.yScale(d.name)) || 0) + this.PADDING_TOP_GRID
      )
      .attr("r", 8)
      .attr("class", (record: Record) =>
        [
          "text",
          ...Object.entries(record.context).map(
            ([key, value]) => `text-${key}-${value}`
          )
        ].join(" ")
      )
      .on(
        "click",
        (
          record: Record,
          index: number,
          circles: SVGCircleElement[] | ArrayLike<SVGCircleElement>
        ) => {
          this.select(record.raw);

          Array.from(circles).forEach(circle => (circle.style.stroke = "0"));

          circles[index].style.stroke = "rgba(0, 0, 0, 0.5)";
        }
      );

    const node = canvas.node();

    if (node) {
      this.height = node.getBBox().height;
    }
  }
}
